import React from "react";
import { getOptimizedVersion } from "../services/processOptimizationService";
import { requestProcessVersion } from "../services/processService";
import { resolveRoleName } from "../utils/auth";
import { buildVersionPayload } from "../utils/processOptimizationHelpers";
import {
  createRedesignScenario,
  applyHeuristic,
  getRedesignScenarios,
  deleteRedesignScenario,
} from "../services/redesignScenariosService";
import { getRedesignHeuristics } from "../services/redesignHeuristicsService";
import {
  ENGINE_LABELS,
  readAdoptableHeuristics,
  readAppliedHeuristics,
  readEngineDetail,
  readProvenanceEngines,
  isEngineApplied,
  formatEngineComposition,
} from "../utils/redesignProvenance";

// The engine heuristics that landed as structure, and the engine names to credit on
// the TO-BE card. See persistRedesignScenario for why the not-persistable rows are
// subtracted rather than linked.
//
// `applied_heuristics` is preferred over each engine's `applied_heuristic_codes`
// because it is the only one the analyst's adopt selection has been applied to.
// Once a heuristic can be unticked, the per-engine code list stops meaning "what
// this save wrote" and starts meaning "what this engine came up with" — crediting
// the saved scenario from that list would link heuristics the analyst explicitly
// declined. The old path stays as the fallback for a backend that predates the
// checklist, where the two lists are the same thing.
function buildEngineCredit(provenance) {
  if (!provenance) return null;
  const engines = readProvenanceEngines(provenance);
  const rows = readAppliedHeuristics(provenance);

  const structuralCodes = [];
  if (rows.length) {
    const seen = new Set();
    for (const row of rows) {
      if (!row?.code || row.source === "agent") continue;
      if (row.status !== "saved_as_structure") continue;
      const dedupe = `${row.source}:${row.code}`;
      if (seen.has(dedupe)) continue;
      seen.add(dedupe);
      structuralCodes.push({ code: row.code, engineSource: row.source });
    }
  } else {
    const notPersistable = new Set(
      readAdoptableHeuristics(provenance)
        .filter(isEngineApplied)
        .map((r) => r.code)
        .filter(Boolean),
    );
    for (const engine of engines) {
      if (engine === "agent") continue;
      const detail = readEngineDetail(provenance, engine);
      if (!detail || detail.status === "failed") continue;
      for (const code of detail.applied_heuristic_codes || []) {
        if (!code || notPersistable.has(code)) continue;
        structuralCodes.push({ code, engineSource: engine });
      }
    }
  }
  return {
    engines: engines.map((e) => ENGINE_LABELS[e] || e),
    structuralCodes,
  };
}

// Best-effort: record the agent's AS-IS/TO-BE Devil's Quadrangle result as a
// redesign_scenario pair so it shows up (read-only) under the process's
// "Redesign Scenarios" tab. Never blocks the version save.
const _num = (v) =>
  v === "" || v == null || Number.isNaN(Number(v)) ? undefined : Number(v);

async function persistRedesignScenario(processId, report, flow, overrides = {}, adopted = [], engineCredit = null) {
  if (!report || !report.dimensions) return;
  // ONE current redesign per process: each save REPLACES the previous AS-IS/TO-BE
  // pair instead of accumulating a new pair per save. TO_BE rows go first (they
  // reference their AS_IS baseline), then the rest. Best-effort — a failed delete
  // never blocks recording the new pair.
  try {
    const existing = await getRedesignScenarios(processId);
    const rows = Array.isArray(existing?.items) ? existing.items : Array.isArray(existing) ? existing : [];
    const ordered = [...rows.filter((s) => s.kind === "TO_BE"), ...rows.filter((s) => s.kind !== "TO_BE")];
    for (const s of ordered) {
      try { await deleteRedesignScenario(s.id); } catch (_) {}
    }
  } catch (_) {}
  const t = report.dimensions.time || {};
  const c = report.dimensions.cost || {};
  const q = report.dimensions.quality || {};
  const f = report.dimensions.flexibility || {};
  const asF = flow?.as_is || {};
  const tbF = flow?.to_be || {};
  const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");

  // Record ALL six Devil's-Quadrangle metrics (previously only time & cost were saved),
  // honoring any user override of the quality/flexibility scores.
  const asIs = await createRedesignScenario(processId, {
    kind: "AS_IS",
    name: `AS-IS (optimization ${stamp})`,
    snapshotFromFlowAnalysis: false,
    cycleTimeMinutes: _num(t.as_is),
    laborCost: _num(c.as_is),
    qualityScore: _num(overrides.quality_as_is ?? q.as_is),
    flexibilityScore: _num(overrides.flexibility_as_is ?? f.as_is),
    cycleTimeEfficiency: _num(asF.cycle_time_efficiency_percent),
    valueAddingTimeMinutes: _num(asF.value_adding_time_minutes),
  });
  // The card's figures come from the agent's redesign_report/redesign_flow and stay
  // there even when engines composed the save. They describe the SAVED structure —
  // the structural subset — whereas an engine's self-reported TO-BE describes its
  // own fuller redesign, most of which a process version cannot carry. Swapping them
  // in would make this card disagree with the Flow Analysis tab of the very version
  // it describes. For the same reason two engines' percentages are never added:
  // agent+RL is one combined measurement of one combined structure, not 8% + 5%.
  //
  // The composition sentence is ALWAYS written, agent-only saves included, and always
  // through formatEngineComposition — the Redesign Scenarios tab parses it back out to
  // badge the card, and an unlabelled card there cannot be told apart from an
  // Agent+RL one. Writing it unconditionally is what makes "no badge" mean "saved
  // before this existed" rather than "we forgot".
  const engineNote = ` ${formatEngineComposition(engineCredit?.engines)} Figures describe the saved structure; each engine's own reported improvement is shown on its Redesign tab.`;
  const toBe = await createRedesignScenario(processId, {
    kind: "TO_BE",
    name: `TO-BE (optimization ${stamp})`,
    description: `${report.summary || ""}${engineNote}`.trim() || undefined,
    baselineScenarioId: asIs.id,
    snapshotFromFlowAnalysis: false,
    cycleTimeMinutes: _num(t.to_be),
    laborCost: _num(c.to_be),
    qualityScore: _num(overrides.quality_to_be ?? q.to_be),
    flexibilityScore: _num(overrides.flexibility_to_be ?? f.to_be),
    cycleTimeEfficiency: _num(tbF.cycle_time_efficiency_percent),
    valueAddingTimeMinutes: _num(tbF.value_adding_time_minutes),
  });

  // Agent-applied heuristics + recommendations the analyst explicitly adopted in the
  // Redesign tab (deduped by code) are all linked to the TO-BE scenario, plus the
  // engine heuristics that actually landed as structure. `structuralCodes` is
  // deliberately the engine's applied set MINUS its not-persistable rows: linking a
  // heuristic the version doesn't carry would credit the saved structure with a
  // change that isn't in it — the exact confusion the adopt list exists to prevent.
  const seen = new Set();
  const toLink = [];
  for (const h of [
    ...(report.applied_heuristics || []),
    ...(engineCredit?.structuralCodes || []),
    ...adopted,
  ]) {
    if (!h?.code || seen.has(h.code)) continue;
    seen.add(h.code);
    toLink.push(h);
  }
  if (toLink.length) {
    const codeToId = {};
    try {
      const hx = await getRedesignHeuristics();
      (hx.items || []).forEach((h) => {
        codeToId[h.code] = h.id;
      });
    } catch (_) {}
    for (const h of toLink) {
      const id = codeToId[h.code];
      if (!id) continue;
      try {
        await applyHeuristic(toBe.id, {
          heuristicId: id,
          note: h.adopted
            ? `Adopted by analyst: ${h.rationale || h.basis || ""}`.trim()
            : h.engineSource
              ? `Applied by ${ENGINE_LABELS[h.engineSource] || h.engineSource}: ${h.rationale || h.basis || ""}`.trim()
              : h.rationale,
        });
      } catch (_) {}
    }
  }
}

export default function useOptimizedVersionSave({
  selectedId,
  setErrorModal,
  redesignReport,
  redesignFlow,
  qfOverrides,
  adoptedHeuristics,
  // Ref holding the ACCEPTED job reassignments (Analyze-tab selection) in the
  // backend wire shape [{task_id, replace_job_id, new_job_id, role}]. Read at
  // save time so the freshest selection is persisted with the version.
  acceptedReplacementsRef,
  // The engine-composed payload already fetched and DESCRIBED to the analyst by the
  // Save tab's engine selector, or null when only the agent is selected. Reusing it
  // rather than re-fetching is the point: a fresh fetch could reuse an expired
  // reference and re-run the engine, so the version written would not be the one the
  // confirm step just summarised.
  enginePayload,
}) {
  const [saving, setSaving] = React.useState(false);
  const [saveModal, setSaveModal] = React.useState({
    open: false,
    title: "",
    message: "",
  });
  // The version just created (admin path) — lets the UI close the loop:
  // show the badge and jump straight to the new version.
  const [savedVersion, setSavedVersion] = React.useState(null);

  const saveOptimizedVersion = React.useCallback(async () => {
    const pid = selectedId;
    if (!pid) return;

    setSaving(true);
    try {
      const optimized = enginePayload || (await getOptimizedVersion(pid));
      const payload = buildVersionPayload(optimized);
      // Rides along on the POST untouched — the CMS ignores keys it doesn't know,
      // and keeping it on the request records which engines produced this version.
      const provenance = optimized?.redesign_provenance || null;
      if (provenance) payload.redesign_provenance = provenance;
      const raw = sessionStorage.getItem("auth") || localStorage.getItem("auth");
      let submittedBy = undefined;
      let roleNorm = "";
      try {
        const auth = raw ? JSON.parse(raw) : null;
        submittedBy = auth?.user?.user_id ?? auth?.user?.id;
        const rn = auth?.user ? await resolveRoleName(auth.user) : "";
        roleNorm = String(rn || "")
          .toUpperCase()
          .replace(/[^A-Z]/g, "");
      } catch (_) {}

      if (submittedBy != null) payload.submitted_by = Number(submittedBy);
      // Persist the ACCEPTED job reassignments with the version: the backend
      // clones each affected task (task_version+1) and applies the swap to the
      // clone's job↔task rows — the original version's staffing never changes.
      const accepted = acceptedReplacementsRef?.current || [];
      if (accepted.length > 0) payload.job_reassignments = accepted;
      const created = await requestProcessVersion(pid, payload);
      const newVersion =
        created && created.savedTo === "process"
          ? { process_id: created.process_id, process_version: created.process_version, process_code: created.process_code }
          : null;
      setSavedVersion(newVersion);
      // Record the AS-IS/TO-BE redesign scenario (non-fatal).
      try {
        const adopted = Object.values(adoptedHeuristics || {}).map((h) => ({
          ...h,
          adopted: true,
        }));
        await persistRedesignScenario(
          pid,
          redesignReport,
          redesignFlow,
          qfOverrides || {},
          adopted,
          buildEngineCredit(provenance),
        );
      } catch (e) {
        console.warn("Redesign scenario not persisted:", e?.message);
      }
      const isAdmin = roleNorm === "SUPERADMIN" || roleNorm === "ADMIN";
      const sc = created?.staffing_changes;
      const staffingNote = sc
        ? ` ${sc.applied} accepted job reassignment(s) were persisted into the new version's staffing (${(sc.cloned_tasks || []).length} task(s) re-versioned).${(sc.warnings || []).length ? ` ${sc.warnings.length} warning(s) — see console.` : ""}`
        : accepted.length > 0
          ? " Note: this backend did not report persisted staffing — job reassignments may not be applied (older backend?)."
          : "";
      if (sc?.warnings?.length) console.warn("Version staffing warnings:", sc.warnings);
      setSaveModal({
        open: true,
        title: isAdmin ? "Process Saved" : "Submitted for Approval",
        message:
          isAdmin ?
            created && created.savedTo === "process"
              ? `Saved as version v${created.process_version} of ${created.process_code || "this process"}. The original version is untouched — both are listed on the Process Optimization page and in History.${staffingNote}`
              : "Optimized process version saved successfully."
          : `Optimized process version submitted for admin approval.${accepted.length > 0 ? " Job reassignments are persisted only on direct (admin) commit." : ""}`,
      });
    } catch (e) {
      console.error("Failed to save optimized version", e);
      setErrorModal({
        open: true,
        title: "Failed to Save",
        message: e?.message || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  }, [selectedId, setErrorModal, redesignReport, redesignFlow, qfOverrides, adoptedHeuristics, acceptedReplacementsRef, enginePayload]);

  // The confirm gate. It lives here rather than in the Save tab's markup so there is
  // exactly one path to the POST: whenever engines composed this payload, the
  // analyst sees what of the redesign they reviewed will actually be written before
  // anything is. Agent-only saves have nothing to reconcile and go straight through,
  // which keeps today's Save tab behaviour unchanged.
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const requestSave = React.useCallback(() => {
    if (enginePayload?.redesign_provenance) setConfirmOpen(true);
    else saveOptimizedVersion();
  }, [enginePayload, saveOptimizedVersion]);

  const confirmSave = React.useCallback(async () => {
    setConfirmOpen(false);
    await saveOptimizedVersion();
  }, [saveOptimizedVersion]);

  return {
    saving,
    saveModal,
    setSaveModal,
    saveOptimizedVersion,
    savedVersion,
    confirmOpen,
    setConfirmOpen,
    requestSave,
    confirmSave,
  };
}
